import React, { useRef } from 'react';
import { UploadCloud, SendHorizonal } from 'lucide-react';
import { FileUploadState } from '../../types/project';

interface FileUploadAreaProps {
    fileUploadState: FileUploadState;
    onFileSelect: (file: File) => void;
    onDrop: (file: File) => void;
    onGenerate: () => void;
    onAcknowledgedChange: (acknowledged: boolean) => void;
    acceptAudio?: boolean;
    masteringJobId?: string | null;
    isWebSocketConnected?: boolean;
    buttonText?: string;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
    fileUploadState,
    onFileSelect,
    onDrop,
    onGenerate,
    onAcknowledgedChange,
    acceptAudio = false,
    masteringJobId,
    isWebSocketConnected = true,
    buttonText,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onFileSelect(file);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            onDrop(file);
        }
    };

    const handleBrowseClick = () => fileInputRef.current?.click();

    const getUploadText = () => {
        if (acceptAudio) {
            return "Drag and drop your audio file here";
        }
        return "Drag and drop your JSON file here";
    };

    const getAcceptType = () => {
        if (acceptAudio) {
            return "audio/*,.mp3,.wav,.m4a,.aac";
        }
        return "application/json";
    };

    if (!fileUploadState.selectedFile) {
        return (
            <div
                onDrop={ handleDrop }
                onDragOver={ (e) => e.preventDefault() }
                onClick={ handleBrowseClick }
                className="mt-8 w-full max-w-3xl cursor-pointer border-2 border-dashed border-border rounded-2xl p-12 text-center hover:bg-accent transition"
            >
                <div className="flex flex-col items-center justify-center">
                    <UploadCloud className="w-12 h-12 text-muted-foreground" />
                    <p className="text-base text-card-foreground mt-4">{ getUploadText() }</p>
                    <p className="text-sm text-card-foreground">
                        <span className="underline">or click to browse</span>
                    </p>
                    { acceptAudio && (
                        <p className="text-xs text-muted-foreground mt-2">
                            Supports MP3, WAV, M4A, AAC files
                        </p>
                    ) }
                </div>
                <input
                    ref={ fileInputRef }
                    type="file"
                    accept={ getAcceptType() }
                    className="hidden"
                    onChange={ handleFileChange }
                />
            </div>
        );
    }

    return (
        <div className="mt-8 w-full max-w-3xl border-2 border-dashed border-border rounded-2xl p-8 text-center">
            <div className="flex flex-col items-center justify-center gap-2">
                <UploadCloud className="w-12 h-12 text-muted-foreground mb-2" />
                <p className="text-sm text-card-foreground break-all text-center">
                    { fileUploadState.selectedFile.name }
                </p>
                <p className="text-xs text-muted-foreground">
                    { (fileUploadState.selectedFile.size / 1024 / 1024).toFixed(2) } MB
                </p>

                <label className="flex items-start gap-2 mt-4 text-sm text-card-foreground cursor-pointer max-w-md text-left">
                    <input
                        type="checkbox"
                        checked={ fileUploadState.acknowledged }
                        onChange={ (e) => onAcknowledgedChange(e.target.checked) }
                        className="mt-1 accent-primary"
                    />
                    <span>
                        I hereby acknowledge that I either own or have obtained the rights to use this file
                    </span>
                </label>

                {/* WebSocket Warning */ }
                { acceptAudio && !isWebSocketConnected && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                            <span className="text-sm text-yellow-700">
                                Warning: WebSocket connection is not available. Audio processing may not work properly.
                            </span>
                        </div>
                    </div>
                ) }

                { (fileUploadState.projectId || acceptAudio) && (
                    <button
                        onClick={ onGenerate }
                        className={ `mt-4 px-6 py-2 rounded flex items-center justify-center gap-2 text-sm font-medium btn-primary ${fileUploadState.loading ? 'opacity-100' : ''
                            }` }
                        disabled={ !fileUploadState.acknowledged || fileUploadState.loading }
                    >
                        { fileUploadState.loading ? (
                            <>
                                <svg className="animate-spin w-5 h-5 text-white" viewBox="0 0 24 24">
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        fill="none"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    />
                                </svg>
                                { buttonText ? `${buttonText}...` : (acceptAudio ? 'Mastering...' : 'Processing...') }
                            </>
                        ) : (
                            <>
                                <SendHorizonal className="w-4 h-4" />
                                { buttonText || (acceptAudio ? 'Master Track' : 'Generate') }
                            </>
                        ) }
                    </button>
                ) }

                { acceptAudio && masteringJobId && fileUploadState.loading && (
                    <div className="mt-4 text-center">
                        <p className="text-sm text-muted-foreground">
                            Job ID: { masteringJobId }
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            Waiting for processing to complete...
                        </p>
                    </div>
                ) }
            </div>
        </div>
    );
}; 